import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Card,
  CardContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SecurityIcon from '@mui/icons-material/Security';
import DownloadIcon from '@mui/icons-material/Download';
import api from '../api';

interface DetectedLicense {
  skuPartNumber: string;
  skuName: string;
  consumedUnits: number;
  enabledUnits: number;
}

interface RecommendedPolicy {
  policyID: number;
  policyName: string;
  displayName: string;
  platform: string;
  policyType: string;
  licenseRequirement: string;
  phase: string;
  summaryNotes?: string;
  isDeployed: boolean;
}

interface DeployedPolicy {
  policyID: number;
  policyName: string;
  displayName: string;
  platform: string;
  policyType: string;
  deployedDate?: string;
}

interface GapPolicy {
  policyID: number;
  policyName: string;
  displayName: string;
  platform: string;
  policyType: string;
  licenseRequirement: string;
  phase: string;
  summaryNotes?: string;
  priority: string;
}

interface PolicyGapAssessment {
  customerID: number;
  companyName: string;
  tenantId: string;
  assessmentDate: string;
  detectedLicenses: DetectedLicense[];
  servicePlans: string[];
  recommendedPolicies: RecommendedPolicy[];
  deployedPolicies: DeployedPolicy[];
  policyGaps: GapPolicy[];
  totalRecommendedPolicies: number;
  totalDeployedPolicies: number;
  totalGaps: number;
  coveragePercentage: number;
}

export default function PolicyGapReport() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState<PolicyGapAssessment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchGapAssessment();
    }
  }, [id]);

  const fetchGapAssessment = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get(`/TCAdminPolicyGapAssessment/${id}`);

      setAssessment(response.data);
    } catch (err: any) {
      setError(err.response?.data || 'Failed to load policy gap assessment');
      console.error('Error fetching gap assessment:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'high':
        return <ErrorIcon fontSize="small" />;
      case 'medium':
        return <WarningIcon fontSize="small" />;
      default:
        return <CheckCircleIcon fontSize="small" />;
    }
  };

  const handleExportReport = () => {
    // TODO: Implement PDF export functionality
    alert('PDF export coming soon!');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!assessment) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">No assessment data available</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/customers')}
          sx={{ textDecoration: 'none', color: 'primary.main', cursor: 'pointer' }}
        >
          Customers
        </Link>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate(`/customers/${id}`)}
          sx={{ textDecoration: 'none', color: 'primary.main', cursor: 'pointer' }}
        >
          {assessment.companyName}
        </Link>
        <Typography color="text.primary">Policy Gap Assessment</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AssessmentIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4">Policy Gap Assessment</Typography>
            <Typography variant="body2" color="text.secondary">
              {assessment.companyName} • Generated {formatDate(assessment.assessmentDate)}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleExportReport}
        >
          Export Report
        </Button>
      </Box>

      {/* Coverage Summary */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Recommended Policies
              </Typography>
              <Typography variant="h4">{assessment.totalRecommendedPolicies}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Deployed Policies
              </Typography>
              <Typography variant="h4" color="success.main">
                {assessment.totalDeployedPolicies}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Policy Gaps
              </Typography>
              <Typography variant="h4" color="error.main">
                {assessment.totalGaps}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Coverage
              </Typography>
              <Typography variant="h4" color={assessment.coveragePercentage >= 80 ? 'success.main' : 'warning.main'}>
                {assessment.coveragePercentage}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={assessment.coveragePercentage}
                sx={{ mt: 1 }}
                color={assessment.coveragePercentage >= 80 ? 'success' : 'warning'}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Detected Licenses */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon /> Detected Licenses
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            {assessment.detectedLicenses.map((license, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {license.skuName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {license.consumedUnits} of {license.enabledUnits} licenses in use
                  </Typography>
                  <Chip label={license.skuPartNumber} size="small" sx={{ mt: 1 }} />
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Policy Gaps by Priority */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" /> Policy Gaps (Recommended but Not Deployed)
          </Typography>
          <Divider sx={{ my: 2 }} />

          {assessment.totalGaps === 0 ? (
            <Alert severity="success" icon={<CheckCircleIcon />}>
              Excellent! All recommended policies are deployed. No gaps found.
            </Alert>
          ) : (
            <>
              {/* High Priority */}
              {assessment.policyGaps.filter(g => g.priority === 'High').length > 0 && (
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <ErrorIcon color="error" />
                      <Typography variant="subtitle1" fontWeight="bold">
                        High Priority Gaps ({assessment.policyGaps.filter(g => g.priority === 'High').length})
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Policy Name</TableCell>
                            <TableCell>Platform</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Phase</TableCell>
                            <TableCell>License Requirement</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {assessment.policyGaps
                            .filter(gap => gap.priority === 'High')
                            .map((gap) => (
                              <TableRow key={gap.policyID}>
                                <TableCell>
                                  <Typography variant="body2" fontWeight="medium">
                                    {gap.displayName}
                                  </Typography>
                                  {gap.summaryNotes && (
                                    <Typography variant="caption" color="text.secondary">
                                      {gap.summaryNotes}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Chip label={gap.platform} size="small" variant="outlined" />
                                </TableCell>
                                <TableCell>{gap.policyType}</TableCell>
                                <TableCell>{gap.phase}</TableCell>
                                <TableCell>{gap.licenseRequirement}</TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Medium Priority */}
              {assessment.policyGaps.filter(g => g.priority === 'Medium').length > 0 && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <WarningIcon color="warning" />
                      <Typography variant="subtitle1" fontWeight="bold">
                        Medium Priority Gaps ({assessment.policyGaps.filter(g => g.priority === 'Medium').length})
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Policy Name</TableCell>
                            <TableCell>Platform</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Phase</TableCell>
                            <TableCell>License Requirement</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {assessment.policyGaps
                            .filter(gap => gap.priority === 'Medium')
                            .map((gap) => (
                              <TableRow key={gap.policyID}>
                                <TableCell>
                                  <Typography variant="body2" fontWeight="medium">
                                    {gap.displayName}
                                  </Typography>
                                  {gap.summaryNotes && (
                                    <Typography variant="caption" color="text.secondary">
                                      {gap.summaryNotes}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Chip label={gap.platform} size="small" variant="outlined" />
                                </TableCell>
                                <TableCell>{gap.policyType}</TableCell>
                                <TableCell>{gap.phase}</TableCell>
                                <TableCell>{gap.licenseRequirement}</TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Low Priority */}
              {assessment.policyGaps.filter(g => g.priority === 'Low').length > 0 && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <CheckCircleIcon color="info" />
                      <Typography variant="subtitle1" fontWeight="bold">
                        Low Priority Gaps ({assessment.policyGaps.filter(g => g.priority === 'Low').length})
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Policy Name</TableCell>
                            <TableCell>Platform</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Phase</TableCell>
                            <TableCell>License Requirement</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {assessment.policyGaps
                            .filter(gap => gap.priority === 'Low')
                            .map((gap) => (
                              <TableRow key={gap.policyID}>
                                <TableCell>
                                  <Typography variant="body2" fontWeight="medium">
                                    {gap.displayName}
                                  </Typography>
                                  {gap.summaryNotes && (
                                    <Typography variant="caption" color="text.secondary">
                                      {gap.summaryNotes}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Chip label={gap.platform} size="small" variant="outlined" />
                                </TableCell>
                                <TableCell>{gap.policyType}</TableCell>
                                <TableCell>{gap.phase}</TableCell>
                                <TableCell>{gap.licenseRequirement}</TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Deployed Policies */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon color="success" /> Deployed Policies
          </Typography>
          <Divider sx={{ my: 2 }} />
          {assessment.deployedPolicies.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No policies deployed yet
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Policy Name</TableCell>
                    <TableCell>Platform</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Deployed Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assessment.deployedPolicies.map((policy) => (
                    <TableRow key={policy.policyID}>
                      <TableCell>{policy.displayName}</TableCell>
                      <TableCell>
                        <Chip label={policy.platform} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{policy.policyType}</TableCell>
                      <TableCell>{formatDate(policy.deployedDate)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
